/**
 * Shared types + helpers used by BOTH document-generation engines:
 *   - `generate-document.ts` (Blocks direct AI, legacy)
 *   - `generate-document-via-agent.ts` (Blocks AI Agent, new)
 *
 * Both engines have the same public signature and produce the same
 * `GenerateDocumentResult` shape — only the underlying AI call differs.
 */

import {
  ADDITIONAL_SIGNATORIES_FIELD_NAME,
  ensureAdditionalSignatoryArray,
  formatAdditionalSignatoriesForPrompt,
  SIGNATORY_PARTY_OPTIONS,
} from '@/modules/templates/lib/signatory-fields';
import type { LegalDocument, SignatoryData } from '@/schemas/schema';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GenerateDocumentOptions {
  templateTitle: string;
  templateSlug: string;
  systemPrompt?: string;
  systemPromptRole?: string;
  formData: Record<string, unknown>;
  signal?: AbortSignal;
}

export interface GenerateDocumentResult {
  document: LegalDocument;
  formData: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Default system prompt (used when a template has no custom prompt). The
// agent-backed engine pastes this verbatim into TEMPLATE_INSTRUCTIONS;
// the legacy direct-AI engine prepends it with a role line + appends the
// JSON-output instructions.
// ---------------------------------------------------------------------------

export const DEFAULT_DOCUMENT_SYSTEM_PROMPT = `You are a senior employment attorney and expert legal document drafter with 20+ years of experience. You specialize in creating sophisticated, legally-sound employment agreements that balance employer protection with employee fairness. Your documents are known for their clarity, precision, and professional presentation.

## CRITICAL INSTRUCTIONS

You MUST return ONLY a valid JSON object. Do NOT include markdown code blocks, backticks, or any text outside the JSON structure.

## OUTPUT FORMAT

Return a JSON object matching this EXACT structure:

{
  "metadata": {
    "title": "Employment Agreement",
    "effectiveDate": "YYYY-MM-DD",
    "documentType": "employment-agreement",
    "jurisdiction": "State/Country",
    "generatedAt": "ISO 8601 timestamp"
  },
  "content": [
    {
      "type": "article",
      "props": { "title": "ARTICLE TITLE", "number": "1" },
      "children": [
        {
          "type": "section",
          "props": { "title": "Optional Section Title", "number": "1.1" },
          "children": [
            {
              "type": "paragraph",
              "text": "Content here..."
            }
          ]
        }
      ]
    }
  ]
}

## METADATA REQUIREMENTS

The metadata object MUST contain exactly these fields with the specified formats:
- title: "Employment Agreement" (exact string)
- effectiveDate: ISO date format (YYYY-MM-DD)
- documentType: "employment-agreement" (exact string)
- jurisdiction: State/Country name as string
- generatedAt: ISO 8601 timestamp

## BLOCK STRUCTURE REQUIREMENTS

Strictly follow this hierarchy:
- **Article** (type: "article"): Top-level container
  - props: { "title": string, "number": string }
  - children: Array of Section blocks

- **Section** (type: "section"): Second-level container
  - props: { "title": string | null, "number": string }
  - children: Array of content blocks

- **Content blocks**: paragraph, list, list_item, definition, definition_item
  - paragraph: { "type": "paragraph", "text": string }
  - list: { "type": "list", "props": { "ordered": boolean }, "children": [list_item] }
  - list_item: { "type": "list_item", "text": string }
  - definition: { "type": "definition", "children": [definition_item] }
  - definition_item: { "type": "definition_item", "props": { "term": string }, "text": string }

## CONTENT EXCLUSIONS

Do NOT include signature blocks, "In Witness Whereof" clauses, or any signature-related content in the content array. The signature page is generated separately in the PDF template.

## REGIONAL FORMATTING

Apply formatting conventions appropriate for the jurisdiction specified in the user prompt (dates, currency, addresses, numbers).

## DATA REQUIREMENTS

- Use EXACT names and details provided in the user prompt - never use placeholders or dummy data`;

// ---------------------------------------------------------------------------
// User prompt formatter — turns the form data into a labelled key:value
// list with array/object handling. Both engines feed this into their
// respective user message.
// ---------------------------------------------------------------------------

export function buildDocumentUserPrompt(
  formData: Record<string, unknown>,
  templateTitle: string
): string {
  const sections: string[] = [];
  sections.push(`Generate a ${templateTitle} document with the following information:\n`);

  const formEntries: string[] = [];

  for (const [key, value] of Object.entries(formData)) {
    if (value === null || value === undefined || value === '') continue;

    let formattedValue: string;
    if (typeof value === 'object' && !Array.isArray(value)) {
      formattedValue = JSON.stringify(value, null, 2);
    } else if (Array.isArray(value)) {
      if (key === ADDITIONAL_SIGNATORIES_FIELD_NAME) {
        const entries = ensureAdditionalSignatoryArray(value);
        formattedValue = formatAdditionalSignatoriesForPrompt(entries);
      } else {
        const arePrimitives = value.every(
          (item) => item === null || ['string', 'number', 'boolean'].includes(typeof item)
        );
        formattedValue = arePrimitives ? value.join(', ') : JSON.stringify(value, null, 2);
      }
    } else {
      formattedValue = String(value);
    }

    if (formattedValue) {
      formEntries.push(`${key}: ${formattedValue}`);
    }
  }

  if (formEntries.length > 0) {
    sections.push(formEntries.join('\n'));
    sections.push('');
  }

  sections.push(
    'Please generate a complete, professional legal document incorporating all the above information.'
  );

  return sections.join('\n');
}

// ---------------------------------------------------------------------------
// Signatory extraction — fallback used when the AI omits the signatories
// array. Pulls from several legacy form-field patterns.
// ---------------------------------------------------------------------------

export function extractSignatoriesFromFormData(
  formData: Record<string, unknown>
): SignatoryData[] {
  const signatories: SignatoryData[] = [];

  const normalizeParty = (value: unknown): string => {
    if (typeof value === 'string') {
      const lower = value.toLowerCase();
      if ((SIGNATORY_PARTY_OPTIONS as readonly string[]).includes(lower)) {
        return lower;
      }
    }
    return 'other';
  };

  const normalizeText = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  const appendSignatory = (data: {
    name?: string;
    email?: string;
    party?: unknown;
    title?: string;
    phone?: string;
  }) => {
    const name = normalizeText(data.name);
    const email = normalizeText(data.email);
    if (!name || !email) return;

    signatories.push({
      party: normalizeParty(data.party),
      name,
      email,
      title: normalizeText(data.title),
      phone: normalizeText(data.phone),
    });
  };

  // 1. New Signatory Screen format (formData.signatories array)
  if (Array.isArray(formData.signatories) && formData.signatories.length > 0) {
    for (const entry of formData.signatories as Array<Record<string, unknown>>) {
      appendSignatory({
        party: entry.partyType as unknown,
        name: entry.name as string | undefined,
        email: entry.email as string | undefined,
        title: entry.title as string | undefined,
        phone: entry.phone as string | undefined,
      });
    }
    if (signatories.length > 0) return signatories;
  }

  // 2. Primary signatory fields
  appendSignatory({
    party: formData.party,
    name: String(formData.name || ''),
    email: String(formData.email || ''),
    title: String(formData.title || ''),
    phone: String(formData.phone || ''),
  });

  // 3. Additional signatories repeater
  const additionalEntries = ensureAdditionalSignatoryArray(
    formData[ADDITIONAL_SIGNATORIES_FIELD_NAME]
  );
  for (const entry of additionalEntries) {
    appendSignatory(entry);
  }

  // 4. Pattern-based fields (signatory_1_name, etc.)
  let signatoryIndex = 1;
  while (formData[`signatory_${signatoryIndex}_name`]) {
    appendSignatory({
      party: formData[`signatory_${signatoryIndex}_party`],
      name: String(formData[`signatory_${signatoryIndex}_name`] || ''),
      email: String(formData[`signatory_${signatoryIndex}_email`] || ''),
      title: String(formData[`signatory_${signatoryIndex}_title`] || ''),
      phone: String(formData[`signatory_${signatoryIndex}_phone`] || ''),
    });
    signatoryIndex++;
  }

  if (signatories.length > 0) return signatories;

  // 5. Legacy hardcoded field names
  if (formData.companyRepName || formData.companyName) {
    signatories.push({
      party: 'employer',
      name: String(formData.companyRepName || formData.companyName || 'Company Representative'),
      title: String(formData.companyRepTitle || 'Authorized Representative'),
      email: String(formData.companyRepEmail || formData.companyEmail || ''),
      phone:
        formData.companyRepPhone || formData.companyPhone
          ? String(formData.companyRepPhone || formData.companyPhone)
          : undefined,
    });
  }

  if (formData.employeeName) {
    signatories.push({
      party: 'employee',
      name: String(formData.employeeName),
      title: String(formData.jobTitle || 'Employee'),
      email: String(formData.employeeEmail || ''),
      phone: formData.employeePhone ? String(formData.employeePhone) : undefined,
    });
  }

  return signatories;
}

// ---------------------------------------------------------------------------
// Post-processing — applied to the parsed AI response by BOTH engines so
// metadata + signatories end up consistent regardless of which AI path
// produced the JSON.
// ---------------------------------------------------------------------------

export function finalizeLegalDocument(
  parsed: Partial<LegalDocument>,
  options: { templateTitle: string; templateSlug: string; formData: Record<string, unknown> }
): LegalDocument {
  if (!parsed.metadata || !parsed.content) {
    throw new Error('Invalid document structure: missing metadata or content');
  }

  parsed.metadata.documentType = options.templateSlug;
  parsed.metadata.title = parsed.metadata.title || options.templateTitle;
  parsed.metadata.generatedAt = new Date().toISOString();

  if (!parsed.signatories || parsed.signatories.length === 0) {
    parsed.signatories = extractSignatoriesFromFormData(options.formData);
  }

  return parsed as LegalDocument;
}
